import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { InlineCTA } from "@/components/InlineCTA";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/paver-patio-installation-morris-county-nj-2.webp";
import patioImage from "@/assets/patio-pavers-mountain-lakes-morris-county-nj-3.webp";
import drivewayImage from "@/assets/concrete-driveway-lincoln-park-nj.webp";
import outdoorImage from "@/assets/pavers-installation-morris-county-nj.webp";
import pomptonImage from "@/assets/morris-county-masonry-pavers-pompton-plains-nj.webp";
import denvilleImage from "@/assets/paver-patio-installation-denville-morris-county-nj.webp";

const faqs = [
  {
    question: "Do I need a permit to build a patio in Morris County?",
    answer:
      "Depends on the town and how big the patio is. Morris Township requires a grading permit once you go past 500 square feet of impervious cover. That can mean installing dry wells. Other towns have their own limits. Most will want at least a zoning permit. Check with your town's zoning office or have your contractor look into it before the project starts.",
  },
  {
    question: "How tall can a retaining wall be before you need engineering plans?",
    answer:
      "Four feet. Once you hit that, New Jersey's Uniform Construction Code requires a construction permit and engineered plans from a licensed NJ professional engineer. Below four feet, a zoning permit is usually enough. Some Morris County towns add their own rules on top of the state requirement, so check with the building department.",
  },
  {
    question: "When is the best time to do masonry or paver work in Morris County?",
    answer:
      "April through November is the window for most projects. Concrete and mortar need consistent temperatures above freezing to cure right. We can work in colder months with the right additives and blankets, but it limits what we can do and stretches the timeline. If you have something in mind for spring or summer, getting on the schedule early helps.",
  },
  {
    question: "Does the clay soil here affect how long patios and driveways last?",
    answer:
      "It does. Clay absorbs water and swells up. Then it dries and contracts. That movement stresses anything sitting on top of it. Base prep and drainage are how we deal with that. When that part gets rushed, it is usually why a patio or driveway fails within a few years.",
  },
  {
    question: "Can outdoor kitchens and fireplaces hold up through winter here?",
    answer:
      "If the footings are below 36 inches, yes. That is the frost depth in New Jersey. Below that line, the structure stays put when the ground freezes and thaws. Natural stone and the right grade of brick handle cold temperatures and moisture fine. We have built fireplaces that homeowners use well into December and January.",
  },
  {
    question: "Do the rules for hardscape projects change from town to town?",
    answer:
      "They do. All 39 Morris County municipalities have their own zoning ordinances and permit rules. Coverage limits, setback distances, grading triggers. They all vary. A project that passes in one town might need a variance in the next. Having a contractor who has already worked in your town makes the permit process a lot easier.",
  },
];

const municipalities = [
  "Boonton", "Boonton Township", "Butler", "Chatham Borough", "Chatham Township",
  "Chester Borough", "Chester Township", "Denville", "Dover", "East Hanover",
  "Florham Park", "Hanover", "Harding", "Jefferson", "Kinnelon",
  "Lincoln Park", "Long Hill", "Madison", "Mendham Borough", "Mendham Township",
  "Mine Hill", "Montville", "Morris Plains", "Morris Township", "Morristown",
  "Mountain Lakes", "Mount Arlington", "Mount Olive", "Netcong", "Parsippany-Troy Hills",
  "Pequannock", "Randolph", "Riverdale", "Rockaway Borough", "Rockaway Township",
  "Roxbury", "Victory Gardens", "Washington Township", "Wharton",
];

export default function MorrisCounty() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Masonry Contractor Morris County NJ | Masonry Pro</title>
        <meta name="description" content="Patios sinking? Need a retaining wall on a slope? We handle masonry work across all 39 Morris County towns, built for clay soil and tough North Jersey winters." />
        <meta property="og:description" content="Patios sinking? Need a retaining wall on a slope? We handle masonry work across all 39 Morris County towns, built for clay soil and tough North Jersey winters." />
        <meta property="og:title" content="Masonry Contractor Morris County NJ | Masonry Pro" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
            }))
          })}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/morris-county.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/morris-county.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Masonry contractor serving Morris County, NJ" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <MapPin className="h-6 w-6" style={{ color: '#d4a853' }} />
                  <span style={{ color: '#d4a853', fontWeight: 500 }}>Morris County, NJ</span>
                </div>
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Masonry and Hardscape Installation in{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Morris County, NJ</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', maxWidth: '42rem', margin: '0 auto', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Serving all 39 municipalities with expert hardscape and masonry work built for Morris County's clay soil and terrain.
                </p>
              </div>
            </div>
          </section>

          {/* Intro Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-6 text-lg text-muted-foreground">
                <p>
                  There are 39 municipalities in{" "}
                  <a href="https://en.wikipedia.org/wiki/Morris_County,_New_Jersey" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">Morris County</a><sup className="text-secondary">1</sup>.{" "}
                  Morristown, Parsippany, Chester, Denville, Randolph, and each one of them has different lot conditions, different soil, and different building codes.
                </p>
                <p>
                  If you are planning a{" "}
                  <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">paver patio</Link>,{" "}
                  <Link to="/paving-services/paver-walkways" className="text-secondary hover:underline">paver walkway</Link>,{" "}
                  <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">retaining wall</Link>,{" "}
                  <Link to="/concrete-services/driveways" className="text-secondary hover:underline">concrete driveway</Link>, or{" "}
                  <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">outdoor kitchen</Link>,{" "}
                  the contractor you hire should already be familiar with this part of North Jersey. Not learning it on your project.
                </p>
                <p>
                  The ground in Morris County is mostly glacial till, which is a mixture of heavy clay, sand, gravel, and rock that was left by retreating glaciers<sup className="text-secondary">2</sup>. Clay is the biggest problem because it holds water, swells up, and shrinks back, and winter can make it worse with the freeze-thaw cycles.
                </p>
                <p>
                  Permits are another thing because each of the 39 towns writes its own zoning and grading rules. That means Morristown is different from Denville, and Denville is different from Randolph. If someone gets any of this wrong, the project can stall before it even starts.
                </p>
                <p>
                  Biagio has been doing masonry work for over 35 years, and Gino has over 14 years in the field. Between the two of them, they have been on properties across Morris County long enough to know what the ground is going to do before the first shovel goes in.
                </p>
              </div>
            </div>
          </section>

          {/* CTA 1 */}
          <InlineCTA heading="Planning a Project?" highlightedHeading="Check Seasonal Availability" description="Call or text us directly or scroll down to request your free estimate." />

          {/* Paver Patios & Walkways Section - image left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={patioImage} alt="Luxury patio pavers with fire pit and pool in Mountain Lakes, Morris County, NJ by Masonry Pro Construction" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Paver Patios and Walkways Built for{" "}
                    <span className="text-gradient-gold">Morris County Ground</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      <Link to="/paving-services/paver-patios" className="text-secondary hover:underline">Paver patios</Link> are a big request for us. In towns like Morristown, Madison, and Chatham, people want a spot for the grill, a table, and maybe a fire pit out back. Picking the pavers can be the fun part. What makes or breaks the project is the base.
                    </p>
                    <p>
                      Most of Morris County sits on that clay-heavy glacial till. Water gets in, the clay swells, then it freezes and expands more. Northern New Jersey can experience 60 or more freeze-thaw cycles in a single winter<sup className="text-secondary">3</sup>. A patio with a thin or rushed base shows damage within two or three years. Shifting, settling, uneven spots.
                    </p>
                    <p>
                      We dig to the right depth and compact the aggregate in lifts. Then a gravel base goes down that actually drains. The Interlocking Concrete Pavement Institute says pedestrian areas need around 4 inches of compacted base on well-drained soil, depending on the foundation already in place<sup className="text-secondary">4</sup>. Morris County soil is not particularly well-drained. This means we need to go heavier on the base here, because the clay doesn't give you any room to cut corners.
                    </p>
                    <p>
                      You will never see any of this once the pavers are laid, and that's the whole point. Spring runoff can hit Denville and Parsippany pretty hard. If drainage is not part of the base plan, water can pool underneath the pavers or sit on the surface. We grade for that, so it won't happen.
                    </p>
                    <p>
                      If you're still working out a budget, here's a closer look at <Link to="/pro-tips/paver-patio-cost-north-jersey" className="text-secondary hover:underline">what affects paver patio pricing</Link> across North Jersey.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Retaining Walls Section - text left, image right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Retaining Walls That Hold on{" "}
                    <span className="text-gradient-gold">Morris County Clay</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Hilly lots in Randolph, Denville, and Chester make it harder to use the backyard. A <Link to="/masonry-services/retaining-walls" className="text-secondary hover:underline">retaining wall</Link> can level out a slope and give you room for a patio, a garden, or just a flat area for the kids.
                    </p>
                    <p>
                      Clay makes retaining walls trickier because it will trap water behind the wall. Rain soaks in, and the clay will hold it, causing pressure to build. A wall without proper drainage behind it will start to bow over time. Give it a few years, and it fails.
                    </p>
                    <p>
                      We put footing drains wrapped in filter fabric behind almost every wall we build. Clean stone backfill gives the water somewhere to go. Nobody photographs the drain system. But it is the reason the wall stays straight.
                    </p>
                    <p>
                      Height changes what permits you need. If the wall is under four feet, most Morris County towns will want a zoning permit. If it's four feet or above, you are most likely looking at a NJ construction permit<sup className="text-secondary">5</sup>. Which also means walls at that height will need engineered drawings from a licensed NJ professional engineer<sup className="text-secondary">6</sup>.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={denvilleImage} alt="Paver patio installation surrounding a pool in Denville, Morris County, NJ by Masonry Pro Construction" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          </section>

          {/* Concrete Driveways & Sidewalks - image left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={drivewayImage} alt="Newly installed paver driveway in front of a gray house in Morris Township, Morris County, NJ by Masonry Pro Construction" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Concrete Driveways and Sidewalks{" "}
                    <span className="text-gradient-gold">Poured for North Jersey Weather</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      November through March is rough on concrete in Morris County. Freeze-thaw can open cracks, and road salt from municipal plows can wear down the surface. Once it starts, there is no way you can undo it.
                    </p>
                    <p>
                      The reality is that concrete is going to crack. You can't stop it. What you can do is control where it's going to crack. By putting in control joints, you give the slab a weak point on purpose so the crack follows the joint line instead of running across the <Link to="/concrete-services/driveways" className="text-secondary hover:underline">driveway</Link>.
                    </p>
                    <p>
                      We reinforce the pour and space the joints to manage it. Under the slab, the subgrade receives extra attention due to the clay. If compaction is not right, the ground can shift with the seasons, and settlement cracks can show up a year later.
                    </p>
                    <p>
                      Belgium block edging is popular in Florham Park, Mendham, and Mountain Lakes. Gives the driveway a clean finished look and keeps the edges from chipping.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA 2 */}
          <InlineCTA heading="Have Questions?" highlightedHeading="Let's Talk About Your Project" description="Give us a call or text, or fill out the form below to get honest advice on your project." />

          {/* Outdoor Kitchens & Fireplaces - text left, image right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Outdoor Kitchens and Fireplaces{" "}
                    <span className="text-gradient-gold">for Year-Round Use</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      We are doing more <Link to="/masonry-services/outdoor-kitchens" className="text-secondary hover:underline">outdoor kitchens</Link> and <Link to="/masonry-services/fire-pits" className="text-secondary hover:underline">fireplaces</Link> in Chatham, Morristown, and East Hanover than we used to. It is not just a summer thing anymore. Most people want to use the space from April through November, and sometimes you can light up the fireplace on a milder January weekend.
                    </p>
                    <p>
                      These structures all need real footings for long-term support. New Jersey's frost depth is 36 inches. If you go shallower, the structure can move when the ground freezes. That shifting can cause the countertops to crack, and the grills can shift out of alignment. <Link to="/masonry-services/stone-veneer" className="text-secondary hover:underline">Veneer</Link> pulling away from the base.
                    </p>
                    <p>
                      Outdoor pizza ovens usually require their own permits, whether it's gas or electric, which are separate from the building permit. We line up licensed plumbers and electricians so we can keep everything on schedule.
                    </p>
                    <p>
                      In Harding and Mendham Borough, yards that face north will stay wet longer and get less sun. This can create a slower curing process for the mortar and concrete. Moss usually shows up faster on stone. We help pick materials and schedule the work, keeping that in mind.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={outdoorImage} alt="Newly installed paver patio, walkways, and fire pit in a backyard in Morris County, NJ by Masonry Pro Construction" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          </section>

          {/* Permits Section - image left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img src={pomptonImage} alt="Outdoor patio with masonry pavers and brick steps in Pompton Plains, Morris County, NJ by Masonry Pro Construction" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Permit Rules Across{" "}
                    <span className="text-gradient-gold">Morris County</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      As noted earlier, permits in Morris County can vary across the 39 municipalities, since each has its own zoning, grading, and building permit requirements. What is fine in Parsippany might need a variance in Randolph.
                    </p>
                    <p>
                      Morris Township is a good example. If you add more than 500 square feet of impervious surface to the project, that can trigger a grading permit<sup className="text-secondary">7</sup>. This can affect patios and driveways. The permit may even require a dry well for stormwater. Other towns in the county have similar rules due to the similar soils. The thresholds are just different.
                    </p>
                    <p>
                      Retaining walls can catch homeowners off guard, too. Under four feet, most towns just want a zoning permit. If you go above 4 feet, it may require a construction permit under the NJ Uniform Construction Code<sup className="text-secondary">5</sup>.
                    </p>
                    <p>
                      We have pulled permits in Parsippany, Denville, Randolph, Boonton, and many other Morris County towns. Knowing what each building department expects before we submit saves time and a lot less back and forth for everyone.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Service Area / Getting Here */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-8">
                  Working Across All 39{" "}
                  <span className="text-gradient-gold">Morris County Municipalities</span>
                </h2>
                <div className="space-y-6 text-lg text-muted-foreground text-left">
                  <p>
                    We have done work all over Morris County. Near the Morristown Green historic district. Out in Chester and Long Valley. Newer construction in Mount Olive and Roxbury. The soil, the homes, and the permits differ for each one, and we plan for that before any work starts.
                  </p>
                  <p>
                    Properties near the Frelinghuysen Arboretum, Speedwell Lake Park, and Jockey Hollow tend to sit on thick clay with big root systems running through it. Excavation and drainage are different on those projects.
                  </p>
                  <p>
                    NJ Transit stops in Morristown, Madison, Convent Station, Denville, and Dover put us in easy reach of most of the county.
                  </p>
                  <p>
                    Morris County soils can vary, so each project will have a different set of requirements. Clay pockets in Denville need a different base than rocky hillside lots in Randolph. Older colonials in Madison will have different layout needs than a new build in Chester. Gino and Biagio have seen these conditions in these specific towns. That is why having a local contractor matters.
                  </p>
                </div>
                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-4">All 39 Municipalities We Serve</h3>
                  <div className="flex flex-wrap justify-center gap-3">
                    {municipalities.map((town) => (
                      <span
                        key={town}
                        className="bg-card rounded-full px-4 py-2 border border-border text-sm text-muted-foreground"
                      >
                        {town}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA 3 */}
          <CTASection serviceName="Morris County masonry" heading="Ready to Start?" highlightedHeading="Schedule Your Estimate" />

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection
            videoIds={["FwwKcMlYRRU", "aJJQS6FX--0", "TspgYEFQ_sQ"]}
            title="See Our Morris County"
            highlightedTitle="Work in Action"
          />

          {/* FAQ Section - Card Grid */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-background rounded-xl p-6 border border-border"
                  >
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Citations Accordion */}
          <section className="py-12">
            <div className="container mx-auto px-4 max-w-4xl">
              <Accordion type="single" collapsible>
                <AccordionItem value="citations" className="border-border">
                  <AccordionTrigger className="text-sm text-muted-foreground hover:no-underline">
                    Source Citations
                  </AccordionTrigger>
                  <AccordionContent>
                    <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                      <li>
                        Morris County, New Jersey – Wikipedia – Municipal count and geographic reference.
                      </li>
                      <li>
                        USDA NRCS – Web Soil Survey – Soil data for Morris County including glacial till and clay content.
                      </li>
                      <li>
                        Freeze-Thaw Days in the Northeastern United States – Journal of Applied Meteorology and Climatology (1987) – Freeze-thaw cycle frequency for inland northern New Jersey.
                      </li>
                      <li>
                        ICPI Tech Spec 2 – Construction of Interlocking Concrete Pavements – Base preparation and compaction standards for paver installations.
                      </li>
                      <li>
                        N.J.A.C. 5:23-2.14 – Construction Permits: When Required – NJ Uniform Construction Code retaining wall permit thresholds.
                      </li>
                      <li>
                        N.J. Admin. Code 19:66-7.8 – Retaining Walls – Engineering certification for retaining walls four feet and above.
                      </li>
                      <li>
                        Morris Township, NJ – Zoning and Grading Permit FAQs – Impervious cover thresholds and grading permit requirements.
                      </li>
                    </ol>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
